import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, useMediaQuery, IconButton, Drawer, List, ListItem, ListItemText, FormControl, Select, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const NavBar = () => {
  const { t, language, changeLanguage } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { name: t('counterPicker'), path: '/' },
    { name: t('winRate'), path: '/winrate' },
    { name: t('tierList'), path: '/tierlist' },
  ];

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2, color: 'white', fontWeight: 'bold' }}>
        Marvel Rivals VIP
      </Typography>
      <List>
        {navItems.map((item) => (
          <ListItem 
            key={item.name} 
            component={Link} 
            to={item.path}
            sx={{
              color: isActive(item.path) ? '#fbdb2b' : 'white',
              textDecoration: 'none',
              '&:hover': {
                backgroundColor: 'rgba(251, 219, 43, 0.1)'
              }
            }}
          >
            <ListItemText primary={item.name} />
          </ListItem>
        ))}
      </List>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <FormControl 
          size="small" 
          sx={{ 
            minWidth: 120,
            '& .MuiOutlinedInput-root': {
              color: 'rgba(255,255,255,0.9)',
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(251, 219, 43, 0.5)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#fbdb2b'
              }
            },
            '& .MuiSelect-icon': {
              color: 'rgba(255,255,255,0.7)'
            }
          }}
        >
          <Select
            value={language}
            onChange={(e) => changeLanguage(e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'language selection' }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: '#2b3340',
                  color: 'white',
                  '& .MuiMenuItem-root': {
                    '&:hover': {
                      backgroundColor: 'rgba(251, 219, 43, 0.1)'
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(251, 219, 43, 0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(251, 219, 43, 0.3)'
                      }
                    }
                  }
                }
              }
            }}
            sx={{
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.3)'
              }
            }}
          >
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="es">Español</MenuItem>
            <MenuItem value="zh">中文</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  return (
    <AppBar position="static" sx={{ backgroundColor: '#1e2530', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{
            color: 'white',
            textDecoration: 'none',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              color: '#fbdb2b'
            }
          }}
        >
          Marvel Rivals VIP
        </Typography>

        {isMobile ? (
          <>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ color: 'white' }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile
              }}
              PaperProps={{
                sx: {
                  backgroundColor: '#2b3340',
                  width: 240,
                }
              }}
            >
              {drawer}
            </Drawer>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mr: 2 }}>
              {navItems.map((item) => (
                <Button
                  key={item.name}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: isActive(item.path) ? '#fbdb2b' : 'white',
                    mx: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(251, 219, 43, 0.1)',
                      color: '#fbdb2b'
                    },
                    position: 'relative',
                    '&::after': isActive(item.path) ? {
                      content: '""',
                      position: 'absolute',
                      bottom: 5,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '20px',
                      height: '3px',
                      backgroundColor: '#fbdb2b',
                      borderRadius: '2px'
                    } : {}
                  }}
                >
                  {item.name}
                </Button>
              ))}
            </Box>
            <FormControl 
              size="small" 
              sx={{ 
                minWidth: 120,
                '& .MuiOutlinedInput-root': {
                  color: 'rgba(255,255,255,0.9)',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(251, 219, 43, 0.5)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#fbdb2b'
                  }
                },
                '& .MuiSelect-icon': {
                  color: 'rgba(255,255,255,0.7)'
                }
              }}
            >
              <Select
                value={language}
                onChange={(e) => changeLanguage(e.target.value)}
                displayEmpty
                inputProps={{ 'aria-label': 'language selection' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#2b3340',
                      color: 'white',
                      '& .MuiMenuItem-root': {
                        '&:hover': {
                          backgroundColor: 'rgba(251, 219, 43, 0.1)'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(251, 219, 43, 0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(251, 219, 43, 0.3)'
                          }
                        }
                      }
                    }
                  }
                }}
                sx={{
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.3)'
                  }
                }}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="es">Español</MenuItem>
                <MenuItem value="zh">中文</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default NavBar;